// Animation on the home server: video depth on the GPU (or depth frames made
// in the browser), then server-side rendering and encoding (ffmpeg / NVENC).

import { buildDepthOps } from '../depth/ops';
import type { StudioSettings } from '../state.svelte';
import { form, runJob, type JobView } from '../server/api';
import { batchPool, interactivePool } from '../wasm/pool';
import { loadFont } from '../depth/fonts';
import { processDepth, sourceDepth } from '../pipeline';
import { textAnimationScene } from './textAnim';
import { sceneStore } from '../scene/store.svelte';
import { exportDepthZip } from './export';
import { filterOf, generateDepthFrames, motionOf, planAnimation, type AnimInputs, type Progress } from './generate';
import type { AnimLimits } from './limits';
import type { AnimSettings } from './settings.svelte';

export type ServerFormat = 'gif' | 'webm' | 'mp4' | 'zip';

export interface ServerResult {
  blob: Blob;
  url: string;
  format: ServerFormat;
  frames: number;
  fps: number;
  width: number;
  height: number;
  ms: number;
}

async function texturePng(s: StudioSettings, inputs: AnimInputs): Promise<Blob | undefined> {
  if (s.texture.kind !== 'image' || !inputs.textureImage) return undefined;
  const png = await batchPool().call('encodePng', { image: inputs.textureImage });
  return new Blob([png as Uint8Array<ArrayBuffer>], { type: 'image/png' });
}

export async function runServerAnimation(
  a: AnimSettings,
  s: StudioSettings,
  inputs: AnimInputs,
  limits: AnimLimits,
  format: ServerFormat,
  model: string,
  onProgress: (p: Progress & { queuePosition?: number }) => void,
  signal?: AbortSignal,
): Promise<ServerResult> {
  const t0 = performance.now();
  const plan = planAnimation(a, inputs, limits);
  const elapsed = () => performance.now() - t0;
  const animParams = {
    fps: plan.fps,
    loop_mode: a.loop,
    stereo: s.stereo,
    texture: s.texture,
    // Server replaces `normalize` with a sequence-wide range itself.
    ops: buildDepthOps(s.depth),
    motion: motionOf(a),
    format,
    width: plan.width,
    height: plan.height,
  };
  const texture = await texturePng(s, inputs);
  const track = (v: Partial<JobView> & { eta_ms: number }) =>
    onProgress({
      stage: 'render',
      done: Math.round((v.progress ?? 0) * plan.frames),
      total: plan.frames,
      etaMs: v.eta_ms,
      elapsedMs: elapsed(),
      queuePosition: v.queue_position,
    });
  let view: JobView;
  let blob: Blob;

  if (a.source === 'video') {
    if (!inputs.video) throw new Error('no video loaded');
    // 1. Video -> depth on the server GPU.
    const dj = await runJob(
      'video-depth',
      form({ model, fps: plan.fps, max_seconds: limits.maxSeconds, filter: filterOf(a) }, { video: inputs.video.file }),
      (v) =>
        onProgress({
          stage: 'depth',
          done: Math.round((v.progress ?? 0) * plan.frames),
          total: plan.frames,
          etaMs: v.eta_ms,
          elapsedMs: elapsed(),
          queuePosition: v.queue_position,
        }),
      signal,
    );
    // 2. Render + encode, reusing the depth job (and its colours).
    ({ view, blob } = await runJob(
      'animation',
      form({ ...animParams, depth_job: dj.view.id }, { texture }),
      (v) =>
        onProgress({
          stage: 'render',
          done: Math.round((v.progress ?? 0) * plan.frames),
          total: plan.frames,
          etaMs: v.eta_ms,
          elapsedMs: elapsed(),
          queuePosition: v.queue_position,
        }),
      signal,
    ));
  } else if (a.source === 'text' && a.preset.kind !== 'wave') {
    // 3D text as a scene: the server rasterizes the frames itself.
    const font = await loadFont(s.text.font);
    const scene = textAnimationScene(font, s.text, a.preset, plan.width, plan.height, plan.frames, plan.fps)!;
    ({ view, blob } = await runJob('animation', form({ ...animParams, scene }, { texture }), track, signal));
  } else if (a.source === 'procedural') {
    // Only the parameters (and an optional processed base) travel.
    let base: Blob | undefined;
    if (a.proceduralBase === 'studio') {
      const src = await sourceDepth(interactivePool(), s, inputs, plan.width, plan.height);
      if (src) {
        const processed = await processDepth(interactivePool(), s, src, plan.width, plan.height);
        const png = await interactivePool().call('encodeDepthPng16', { depth: processed });
        base = new Blob([png as Uint8Array<ArrayBuffer>], { type: 'image/png' });
      }
    }
    const params = { ...animParams, ops: [], procedural: { anim: a.procedural, duration: plan.frames / plan.fps } };
    ({ view, blob } = await runJob('animation', form(params, { base, texture }), track, signal));
  } else if (a.source === 'scene') {
    // The server renders the scene timeline itself (stereo-render).
    ({ view, blob } = await runJob(
      'animation',
      form({ ...animParams, scene: sceneStore.current }, { texture }),
      (v) =>
        onProgress({
          stage: 'render',
          done: Math.round((v.progress ?? 0) * plan.frames),
          total: plan.frames,
          etaMs: v.eta_ms,
          elapsedMs: elapsed(),
          queuePosition: v.queue_position,
        }),
      signal,
    ));
  } else {
    // Frames made in the browser (wave text, morph, uploaded sequences) are uploaded.
    const frames = await generateDepthFrames(a, s, inputs, plan, 0, onProgress, signal);
    const zip = await exportDepthZip(frames.depth, () => {});
    // Morph endpoints are already processed in the browser.
    const params = a.source === 'morph' ? { ...animParams, ops: [] } : animParams;
    ({ view, blob } = await runJob(
      'animation',
      form(a.source === 'sequence' ? { ...params, filter: filterOf(a) } : params, { sequence: zip, texture }),
      (v) =>
        onProgress({
          stage: 'render',
          done: Math.round((v.progress ?? 0) * plan.frames),
          total: plan.frames,
          etaMs: v.eta_ms,
          elapsedMs: elapsed(),
          queuePosition: v.queue_position,
        }),
      signal,
    ));
  }
  const meta = (view.meta ?? {}) as { frames?: number; fps?: number; width?: number; height?: number };
  return {
    blob,
    url: URL.createObjectURL(blob),
    format,
    frames: meta.frames ?? plan.frames,
    fps: meta.fps ?? plan.fps,
    width: meta.width ?? plan.width,
    height: meta.height ?? plan.height,
    ms: elapsed(),
  };
}
